import type {
  McpServer,
  SharedIntegration,
  SharedResource,
  SharedResourceQuery,
} from "@rakazo/contracts";
import {
  selectedSharedResources,
  sharedIntegrationProviders,
  toggleSharedResource,
} from "@rakazo/contracts";
import { Button, Checkbox, Input } from "@rakazo/ui-web";
import { ChevronDown, RefreshCw } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { rpc } from "../lib/rpc";

export function SharedBotConnections({
  servers,
  value,
  onChange,
  repos,
  onReposChange,
}: {
  servers: McpServer[];
  value: SharedIntegration[];
  onChange: (value: SharedIntegration[]) => void;
  repos: string[];
  onReposChange: (repos: string[]) => void;
}) {
  const connected = servers.flatMap((server) => {
    const provider = sharedIntegrationProviders.find((p) => p.endpoint === server.endpoint);
    return provider && server.enabled ? [{ server, provider }] : [];
  });
  return (
    <fieldset className="space-y-3">
      <legend className="mb-3 text-sm font-medium">Sources</legend>
      <ResourcePicker
        title="GitHub repositories"
        query={{ provider: "github" }}
        selected={repos.map((id) => ({ id, name: id, region: "us" }))}
        onToggle={(resource, checked) =>
          onReposChange(
            checked
              ? [...new Set([...repos, resource.id])]
              : repos.filter((id) => id.toLowerCase() !== resource.id.toLowerCase()),
          )
        }
      />
      {connected.map(({ server, provider }) => (
        <ResourcePicker
          key={server.id}
          title={`${server.name} ${provider.provider === "linear" ? "teams" : "projects"}`}
          query={{ provider: provider.provider, serverId: server.id }}
          selected={selectedSharedResources(value, server.id)}
          onToggle={(resource, checked) =>
            onChange(toggleSharedResource(value, server.id, provider.provider, resource, checked))
          }
        />
      ))}
      {!connected.length && (
        <p className="text-xs text-muted-foreground">
          Connect Linear, Sentry, or PostHog in Integrations to choose their projects.
        </p>
      )}
    </fieldset>
  );
}
const keyOf = (resource: SharedResource) => `${resource.region}:${resource.id.toLowerCase()}`;
function ResourcePicker({
  title,
  query,
  selected,
  onToggle,
}: {
  title: string;
  query: SharedResourceQuery;
  selected: SharedResource[];
  onToggle: (resource: SharedResource, checked: boolean) => void;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<SharedResource[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const request = useRef<AbortController | null>(null);
  useEffect(() => () => request.current?.abort(), []);
  async function load() {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setLoading(true);
    setError("");
    try {
      const result = await rpc.sharedBots.resources(query, { signal: controller.signal });
      if (!controller.signal.aborted) {
        setItems(result.resources);
        setLoaded(true);
      }
    } catch {
      if (!controller.signal.aborted)
        setError("Could not load this list. Check the connection and refresh.");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }
  const chosen = new Set(selected.map(keyOf));
  const options = [...items, ...selected.filter((s) => !items.some((i) => keyOf(i) === keyOf(s)))];
  const filtered = options.filter((r) =>
    `${r.name} ${r.detail ?? ""} ${r.id}`.toLowerCase().includes(search.toLowerCase()),
  );
  const summary = selected
    .map((s) => items.find((i) => keyOf(i) === keyOf(s))?.name ?? s.name)
    .join(", ");
  return (
    <div className="overflow-hidden rounded-xl border border-border">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 p-4 text-left hover:bg-muted/50"
        aria-expanded={open}
        aria-label={title}
        onClick={() => {
          setOpen(!open);
          if (!open && !loaded && !loading) void load();
        }}
      >
        <span className="min-w-0">
          <span className="block text-sm font-medium">{title}</span>
          <span className="mt-1 block truncate text-xs text-muted-foreground">
            {selected.length ? `${selected.length} selected · ${summary}` : "Choose sources"}
          </span>
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="space-y-3 border-t border-border p-4">
          <div className="flex gap-2">
            <Input
              aria-label={`Search ${title}`}
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={`Refresh ${title}`}
              disabled={loading}
              onClick={() => void load()}
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </Button>
          </div>
          {error && (
            <p role="alert" className="text-xs text-destructive">
              {error}
            </p>
          )}
          {loading && (
            <p role="status" className="text-xs text-muted-foreground">
              Loading…
            </p>
          )}
          <div className="max-h-64 overflow-y-auto">
            {filtered.map((resource) => {
              const checked = chosen.has(keyOf(resource));
              return (
                <label
                  key={keyOf(resource)}
                  htmlFor={`${id}-${keyOf(resource)}`}
                  className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-3 hover:bg-muted"
                >
                  <Checkbox
                    checked={checked}
                    disabled={!checked && selected.length >= 30}
                    onCheckedChange={(value) => onToggle(resource, value === true)}
                    aria-label={resource.name}
                  />
                  <span className="min-w-0">
                    <span className="block break-words text-sm">{resource.name}</span>
                    {resource.detail && (
                      <span className="block text-xs text-muted-foreground">{resource.detail}</span>
                    )}
                  </span>
                </label>
              );
            })}
            {!loading && !error && loaded && !filtered.length && (
              <p className="py-3 text-sm text-muted-foreground">
                {search ? "No matches" : "No resources available to this connection."}
              </p>
            )}
          </div>
          {selected.length >= 30 && (
            <p className="text-xs text-muted-foreground">Up to 30 selections per connection.</p>
          )}
        </div>
      )}
    </div>
  );
}
